import { useEffect, useState, useMemo, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Spinner } from '../components/ui/spinner';
import { Input } from '../components/ui/input';
import { MapLocationButton } from '../components/MapLocationButton';
import { Trees, Route, Compass, MapPin, ArrowUpRight, Mountain, Search, Tent, Users, Calendar, ExternalLink, TreePine, Navigation } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESERVAS_URL = 'https://centralreservas.tenerife.es/';

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const orangeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const violetIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count >= 50 ? 50 : count >= 10 ? 40 : 30;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,0.3);background-color:#22c55e;">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

const createActivityClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count >= 50 ? 50 : count >= 10 ? 40 : 30;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,0.3);background-color:#f97316;">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

const createRecreationalClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count >= 50 ? 50 : count >= 10 ? 40 : 30;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,0.3);background-color:#8b5cf6;">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

// Component to fit map to route bounds
function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);
  return null;
}

// Convert GeoJSON geometry to Leaflet coordinates
const geometryToLatLngs = (geometry) => {
  if (!geometry || !geometry.coordinates) return null;
  if (geometry.type === 'LineString') {
    return geometry.coordinates.map(coord => [coord[1], coord[0]]);
  } else if (geometry.type === 'MultiLineString') {
    return geometry.coordinates.flatMap(line => line.map(coord => [coord[1], coord[0]]));
  }
  return null;
};

export function NaturePage() {
  const { language } = useLanguage();
  
  // Separate data state for each category (lazy loading)
  const [trails, setTrails] = useState([]);
  const [activities, setActivities] = useState([]);
  const [recreationalAreas, setRecreationalAreas] = useState([]);
  
  // Track which categories have been loaded
  const [loadedCategories, setLoadedCategories] = useState({
    trails: false,
    activities: false,
    recreational: false,
  });
  
  // Track which category is currently loading
  const [loadingCategory, setLoadingCategory] = useState(null);
  
  const [activeCategory, setActiveCategory] = useState('activities');
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedTrail, setSelectedTrail] = useState(null);

  const t = {
    title: language === 'es' ? 'Naturaleza' : 'Nature',
    subtitle: language === 'es' ? 'Senderos, rutas, actividades y áreas recreativas' : 'Trails, routes, activities and recreational areas',
    trails: language === 'es' ? 'Senderos' : 'Trails',
    activities: language === 'es' ? 'Actividades' : 'Activities',
    recreationalAreas: language === 'es' ? 'Áreas Recreativas' : 'Recreational Areas',
    allTrails: language === 'es' ? 'Todos' : 'All',
    easy: language === 'es' ? 'Fácil' : 'Easy',
    medium: language === 'es' ? 'Media' : 'Medium',
    hard: language === 'es' ? 'Difícil' : 'Hard',
    difficulty: language === 'es' ? 'Dificultad' : 'Difficulty',
    distance: language === 'es' ? 'Distancia' : 'Distance',
    altitude: language === 'es' ? 'Altitud máx.' : 'Max altitude',
    elevation: language === 'es' ? 'Desnivel' : 'Elevation',
    searchPlaceholder: language === 'es' ? 'Buscar por nombre, municipio o espacio natural...' : 'Search by name, municipality or natural area...',
    searchActivities: language === 'es' ? 'Buscar actividades...' : 'Search activities...',
    searchRecreational: language === 'es' ? 'Buscar áreas recreativas...' : 'Search recreational areas...',
    showing: language === 'es' ? 'Mostrando' : 'Showing',
    of: language === 'es' ? 'de' : 'of',
    results: language === 'es' ? 'senderos' : 'trails',
    activitiesResults: language === 'es' ? 'actividades' : 'activities',
    recreationalResults: language === 'es' ? 'áreas recreativas' : 'recreational areas',
    km: 'km',
    m: 'm',
    viewRoute: language === 'es' ? 'Ver ruta en mapa' : 'View route on map',
    clearRoute: language === 'es' ? 'Limpiar ruta' : 'Clear route',
    maxPeople: language === 'es' ? 'Máx. personas' : 'Max. people',
    overnight: language === 'es' ? 'Pernocta' : 'Overnight',
    groups: language === 'es' ? 'Grupos' : 'Groups',
    availableDays: language === 'es' ? 'Días disponibles' : 'Available days',
    moreInfo: language === 'es' ? 'Más información y reservas' : 'More info & bookings',
    naturalArea: language === 'es' ? 'Espacio Natural' : 'Natural Area',
    pointsOfInterest: language === 'es' ? 'Puntos de Interés' : 'Points of Interest',
    areaType: language === 'es' ? 'Tipo' : 'Type',
    openInGoogleMaps: language === 'es' ? 'Abrir en Google Maps' : 'Open in Google Maps',
    loading: language === 'es' ? 'Cargando...' : 'Loading...',
  };

  // Fetch data for a specific category
  const fetchCategoryData = useCallback(async (category) => {
    if (loadedCategories[category]) return;
    
    setLoadingCategory(category);
    try {
      let endpoint = '';
      switch (category) {
        case 'trails':
          endpoint = '/nature/trails';
          break;
        case 'activities':
          endpoint = '/nature/activities';
          break;
        case 'recreational':
          endpoint = '/nature/recreational-areas';
          break;
        default:
          return;
      }
      
      const response = await axios.get(`${API}${endpoint}`);
      
      switch (category) {
        case 'trails':
          // Filter to senderos only
          const senderos = (response.data || []).filter(t => t.tipo === 'Sendero');
          setTrails(senderos);
          break;
        case 'activities':
          setActivities(response.data || []);
          break;
        case 'recreational':
          setRecreationalAreas(response.data || []);
          break;
      }
      
      setLoadedCategories(prev => ({ ...prev, [category]: true }));
    } catch (error) {
      console.error(`Error fetching ${category}:`, error);
      setLoadedCategories(prev => ({ ...prev, [category]: true }));
    } finally {
      setLoadingCategory(null);
    }
  }, [loadedCategories]);

  // Load activities data on mount (default selected category)
  useEffect(() => {
    fetchCategoryData('activities');
  }, []);

  // Handle category change
  const handleCategoryChange = async (category) => {
    const categoryMap = {
      'trails': 'trails',
      'activities': 'activities',
      'recreational': 'recreational',
    };
    
    setActiveCategory(category);
    setSearch('');
    setActiveFilter('all');
    setSelectedTrail(null);
    
    // Fetch data if not already loaded
    if (!loadedCategories[categoryMap[category]]) {
      await fetchCategoryData(categoryMap[category]);
    }
  };

  const centerPosition = [28.2916, -16.6291];

  const getDifficultyColor = (diff) => {
    switch (diff?.toLowerCase()) {
      case 'baja': case 'low': case 'easy': return 'bg-green-500';
      case 'media': case 'medium': return 'bg-yellow-500';
      case 'alta': case 'high': case 'hard': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getDifficultyLabel = (diff) => {
    if (!diff) return '';
    switch (diff.toLowerCase()) {
      case 'baja': return language === 'es' ? 'Baja' : 'Easy';
      case 'media': return language === 'es' ? 'Media' : 'Medium';
      case 'alta': return language === 'es' ? 'Alta' : 'Hard';
      default: return diff;
    }
  };

  // Filter trails
  const filteredTrails = useMemo(() => {
    let filtered = trails || [];
    if (activeFilter !== 'all') {
      filtered = filtered.filter(t => t.dificultad?.toLowerCase() === activeFilter.toLowerCase());
    }
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(t => 
        (t.nombre || '').toLowerCase().includes(searchLower) ||
        (t.municipio || '').toLowerCase().includes(searchLower) ||
        (t.espacios_naturales || '').toLowerCase().includes(searchLower)
      );
    }
    return filtered;
  }, [trails, activeFilter, search]);

  // Filter activities
  const filteredActivities = useMemo(() => {
    let filtered = activities || [];
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(a => 
        (a.nombre || '').toLowerCase().includes(searchLower) ||
        (a.tipo || '').toLowerCase().includes(searchLower) ||
        (a.infraestructura || '').toLowerCase().includes(searchLower) ||
        (a.descripcion || '').toLowerCase().includes(searchLower)
      );
    }
    return filtered;
  }, [activities, search]);

  // Filter recreational areas
  const filteredRecreationalAreas = useMemo(() => {
    let filtered = recreationalAreas || [];
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(a => 
        (a.nombre || '').toLowerCase().includes(searchLower) ||
        (a.tipo || '').toLowerCase().includes(searchLower) ||
        (a.municipio || '').toLowerCase().includes(searchLower) ||
        (a.espacio_natural || '').toLowerCase().includes(searchLower) ||
        (a.puntos_interes || '').toLowerCase().includes(searchLower)
      );
    }
    return filtered;
  }, [recreationalAreas, search]);

  // Trail markers
  const trailMarkers = useMemo(() => filteredTrails.filter(t => t.lat && t.lon), [filteredTrails]);
  
  // Activity markers
  const activityMarkers = useMemo(() => filteredActivities.filter(a => a.lat && a.lon), [filteredActivities]);
  
  // Recreational markers
  const recreationalMarkers = useMemo(() => filteredRecreationalAreas.filter(a => a.lat && a.lon), [filteredRecreationalAreas]);

  // Handle trail click
  const handleTrailClick = (trail) => {
    if (trail.geometry) {
      const coords = geometryToLatLngs(trail.geometry);
      if (coords) {
        setSelectedTrail({ ...trail, leafletCoords: coords });
      }
    }
  };

  const isLoading = loadingCategory !== null;
  const currentCategoryLoading = loadingCategory === activeCategory || 
    (activeCategory === 'recreational' && loadingCategory === 'recreational');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Trees className="w-5 h-5 text-[#22c55e]" />
          <span className="text-sm font-medium text-[#22c55e] uppercase tracking-wider">
            {language === 'es' ? 'Espacios Naturales' : 'Natural Spaces'}
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#252a34] font-['Outfit'] mb-2">
          {t.title}
        </h1>
        <p className="text-lg text-[#252a34]/70">{t.subtitle}</p>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button
          onClick={() => handleCategoryChange('trails')}
          data-testid="category-trails"
          disabled={loadingCategory === 'trails'}
          className={`flex items-center gap-3 px-5 py-3 rounded-xl text-sm font-medium transition-all ${
            activeCategory === 'trails'
              ? 'bg-[#22c55e] text-white shadow-lg scale-105'
              : 'bg-white text-[#252a34] border border-slate-200 hover:border-[#22c55e] hover:shadow-md'
          }`}
        >
          {loadingCategory === 'trails' ? (
            <Spinner size="sm" className="w-5 h-5" />
          ) : (
            <Route className={`w-5 h-5 ${activeCategory === 'trails' ? 'text-white' : 'text-[#252a34]/60'}`} />
          )}
          <span>{t.trails}</span>
          <Badge 
            variant={activeCategory === 'trails' ? "secondary" : "outline"} 
            className={`ml-1 ${activeCategory === 'trails' ? 'bg-white/20 text-white' : ''}`}
          >
            {loadedCategories.trails ? trails.length : '?'}
          </Badge>
        </button>
        
        <button
          onClick={() => handleCategoryChange('activities')}
          data-testid="category-activities"
          disabled={loadingCategory === 'activities'}
          className={`flex items-center gap-3 px-5 py-3 rounded-xl text-sm font-medium transition-all ${
            activeCategory === 'activities'
              ? 'bg-[#f97316] text-white shadow-lg scale-105'
              : 'bg-white text-[#252a34] border border-slate-200 hover:border-[#f97316] hover:shadow-md'
          }`}
        >
          {loadingCategory === 'activities' ? (
            <Spinner size="sm" className="w-5 h-5" />
          ) : (
            <Compass className={`w-5 h-5 ${activeCategory === 'activities' ? 'text-white' : 'text-[#252a34]/60'}`} />
          )}
          <span>{t.activities}</span>
          <Badge 
            variant={activeCategory === 'activities' ? "secondary" : "outline"} 
            className={`ml-1 ${activeCategory === 'activities' ? 'bg-white/20 text-white' : ''}`}
          >
            {loadedCategories.activities ? activities.length : '?'}
          </Badge>
        </button>
        
        <button
          onClick={() => handleCategoryChange('recreational')}
          data-testid="category-recreational"
          disabled={loadingCategory === 'recreational'}
          className={`flex items-center gap-3 px-5 py-3 rounded-xl text-sm font-medium transition-all ${
            activeCategory === 'recreational'
              ? 'bg-[#8b5cf6] text-white shadow-lg scale-105'
              : 'bg-white text-[#252a34] border border-slate-200 hover:border-[#8b5cf6] hover:shadow-md'
          }`}
        >
          {loadingCategory === 'recreational' ? (
            <Spinner size="sm" className="w-5 h-5" />
          ) : (
            <TreePine className={`w-5 h-5 ${activeCategory === 'recreational' ? 'text-white' : 'text-[#252a34]/60'}`} />
          )}
          <span>{t.recreationalAreas}</span>
          <Badge 
            variant={activeCategory === 'recreational' ? "secondary" : "outline"} 
            className={`ml-1 ${activeCategory === 'recreational' ? 'bg-white/20 text-white' : ''}`}
          >
            {loadedCategories.recreational ? recreationalAreas.length : '?'}
          </Badge>
        </button>
      </div>

      {/* TRAILS SECTION */}
      {activeCategory === 'trails' && (
        <>
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
              <Input
                type="text"
                placeholder={t.searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 bg-white border-slate-200"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'baja', 'media', 'alta'].map((diff) => (
                <button
                  key={diff}
                  onClick={() => setActiveFilter(diff)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeFilter === diff
                      ? 'bg-[#22c55e] text-white'
                      : 'bg-white text-[#252a34] border border-slate-200 hover:border-[#22c55e]'
                  }`}
                >
                  {diff === 'all' ? t.allTrails : getDifficultyLabel(diff)}
                </button>
              ))}
            </div>
          </div>

          <div className="text-sm text-[#252a34]/60 mb-4">
            {t.showing} <strong className="text-[#22c55e]">{filteredTrails.length}</strong> {t.of} {trails.length} {t.results}
          </div>

          {/* Map */}
          <Card className="overflow-hidden border-slate-200 mb-8">
            <div className="h-[350px] md:h-[400px]">
              {currentCategoryLoading ? (
                <div className="flex flex-col items-center justify-center h-full bg-slate-50">
                  <Spinner size="lg" />
                  <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
                </div>
              ) : (
                <MapContainer center={centerPosition} zoom={9} style={{ height: '100%', width: '100%' }} className="z-0">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapLocationButton language={language} />
                  {selectedTrail?.leafletCoords && (
                    <>
                      <Polyline positions={selectedTrail.leafletCoords} pathOptions={{ color: '#22c55e', weight: 4 }}>
                        <Popup>
                          <div className="font-['Outfit'] min-w-[220px]">
                            <h3 className="font-bold text-[#252a34] mb-1">{selectedTrail.nombre}</h3>
                            {selectedTrail.matricula && <Badge className="text-xs mb-2 bg-[#22c55e]">{selectedTrail.matricula}</Badge>}
                            <div className="text-sm text-[#252a34]/70 space-y-1">
                              <div className="flex items-center gap-1">
                                <ArrowUpRight className="w-3 h-3" />
                                <span>{selectedTrail.distancia_km} {t.km}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Mountain className="w-3 h-3" />
                                <span>{selectedTrail.altura_maxima} {t.m}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                <span>{selectedTrail.municipio}</span>
                              </div>
                              {selectedTrail.espacios_naturales && (
                                <div className="flex items-center gap-1">
                                  <Trees className="w-3 h-3 text-green-600" />
                                  <span className="text-green-700">{selectedTrail.espacios_naturales}</span>
                                </div>
                              )}
                            </div>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${selectedTrail.lat},${selectedTrail.lon}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 flex items-center justify-center gap-1 w-full px-3 py-1.5 bg-[#1a73e8] text-xs font-medium rounded hover:bg-[#1557b0] transition-colors"
                              style={{ color: 'white' }}
                            >
                              <Navigation className="w-3 h-3" style={{ color: 'white' }} />
                              {t.openInGoogleMaps}
                            </a>
                          </div>
                        </Popup>
                      </Polyline>
                      <Marker position={[selectedTrail.lat, selectedTrail.lon]} icon={greenIcon}>
                        <Popup>
                          <div className="font-['Outfit'] min-w-[220px]">
                            <h3 className="font-bold text-[#252a34] mb-1">{selectedTrail.nombre}</h3>
                            {selectedTrail.matricula && <Badge className="text-xs mb-2 bg-[#22c55e]">{selectedTrail.matricula}</Badge>}
                            <div className="text-sm text-[#252a34]/70 space-y-1">
                              <div className="flex items-center gap-1">
                                <ArrowUpRight className="w-3 h-3" />
                                <span>{selectedTrail.distancia_km} {t.km}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Mountain className="w-3 h-3" />
                                <span>{selectedTrail.altura_maxima} {t.m}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                <span>{selectedTrail.municipio}</span>
                              </div>
                              {selectedTrail.espacios_naturales && (
                                <div className="flex items-center gap-1">
                                  <Trees className="w-3 h-3 text-green-600" />
                                  <span className="text-green-700">{selectedTrail.espacios_naturales}</span>
                                </div>
                              )}
                            </div>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${selectedTrail.lat},${selectedTrail.lon}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 flex items-center justify-center gap-1 w-full px-3 py-1.5 bg-[#1a73e8] text-xs font-medium rounded hover:bg-[#1557b0] transition-colors"
                              style={{ color: 'white' }}
                            >
                              <Navigation className="w-3 h-3" style={{ color: 'white' }} />
                              {t.openInGoogleMaps}
                            </a>
                          </div>
                        </Popup>
                      </Marker>
                      <FitBounds bounds={selectedTrail.leafletCoords} />
                    </>
                  )}
                  {!selectedTrail && (
                    <MarkerClusterGroup chunkedLoading iconCreateFunction={createClusterIcon} maxClusterRadius={40}>
                      {trailMarkers.map((trail, idx) => (
                        <Marker
                          key={trail.matricula || idx}
                          position={[trail.lat, trail.lon]}
                          icon={greenIcon}
                        >
                          <Popup>
                            <div className="font-['Outfit'] min-w-[220px]">
                              <h3 className="font-bold text-[#252a34] mb-1">{trail.nombre}</h3>
                              {trail.matricula && <Badge className="text-xs mb-2 bg-[#22c55e]">{trail.matricula}</Badge>}
                              <div className="text-sm text-[#252a34]/70 space-y-1">
                                <div className="flex items-center gap-1">
                                  <ArrowUpRight className="w-3 h-3" />
                                  <span>{trail.distancia_km} {t.km}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Mountain className="w-3 h-3" />
                                  <span>{trail.altura_maxima} {t.m}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  <span>{trail.municipio}</span>
                                </div>
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleTrailClick(trail); }}
                                className="mt-2 w-full px-3 py-1.5 bg-[#22c55e] text-white text-xs font-medium rounded hover:bg-green-600 transition-colors"
                              >
                                {t.viewRoute}
                              </button>
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${trail.lat},${trail.lon}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 flex items-center justify-center gap-1 w-full px-3 py-1.5 bg-[#1a73e8] text-xs font-medium rounded hover:bg-[#1557b0] transition-colors"
                                style={{ color: 'white' }}
                              >
                                <Navigation className="w-3 h-3" style={{ color: 'white' }} />
                                {t.openInGoogleMaps}
                              </a>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MarkerClusterGroup>
                  )}
                </MapContainer>
              )}
            </div>
          </Card>

          {selectedTrail && (
            <div className="mb-4">
              <button
                onClick={() => setSelectedTrail(null)}
                className="px-4 py-2 bg-gray-100 text-[#252a34] rounded-lg text-sm hover:bg-gray-200 transition-colors"
              >
                ← {t.clearRoute}
              </button>
            </div>
          )}

          {/* Trail Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentCategoryLoading ? (
              <div className="col-span-full flex flex-col items-center justify-center py-12">
                <Spinner size="lg" />
                <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
              </div>
            ) : (
              filteredTrails.map((trail, idx) => (
                <Card 
                  key={trail.matricula || idx} 
                  className={`card-hover border-slate-200 cursor-pointer transition-all ${
                    selectedTrail?.matricula === trail.matricula ? 'ring-2 ring-[#22c55e]' : ''
                  }`}
                  onClick={() => handleTrailClick(trail)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 rounded-xl bg-[#22c55e] flex items-center justify-center">
                        <Route className="w-6 h-6 text-white" />
                      </div>
                      {trail.dificultad && (
                        <Badge className={`${getDifficultyColor(trail.dificultad)} text-white text-xs`}>
                          {getDifficultyLabel(trail.dificultad)}
                        </Badge>
                      )}
                    </div>
                    <h3 className="font-semibold text-[#252a34] mb-2 font-['Outfit'] line-clamp-2 text-sm">
                      {trail.nombre}
                    </h3>
                    {trail.matricula && (
                      <Badge variant="outline" className="text-xs mb-2 bg-green-50 text-green-700 border-green-200">
                        {trail.matricula}
                      </Badge>
                    )}
                    <div className="grid grid-cols-2 gap-2 text-xs text-[#252a34]/60 mt-3">
                      <div className="flex items-center gap-1">
                        <ArrowUpRight className="w-3 h-3" />
                        <span>{trail.distancia_km} {t.km}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Mountain className="w-3 h-3" />
                        <span>{trail.altura_maxima} {t.m}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 mt-2 text-xs text-[#252a34]/60">
                      <MapPin className="w-3 h-3" />
                      <span className="line-clamp-1">{trail.municipio}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </>
      )}

      {/* ACTIVITIES SECTION */}
      {activeCategory === 'activities' && (
        <>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
              <Input
                type="text"
                placeholder={t.searchActivities}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 bg-white border-slate-200"
              />
            </div>
            <div className="text-sm text-[#252a34]/60 flex items-center gap-2">
              {t.showing} <strong className="text-[#f97316]">{filteredActivities.length}</strong> {t.of} {activities.length} {t.activitiesResults}
            </div>
          </div>

          <Card className="overflow-hidden border-slate-200 mb-8">
            <div className="h-[350px] md:h-[400px]">
              {currentCategoryLoading ? (
                <div className="flex flex-col items-center justify-center h-full bg-slate-50">
                  <Spinner size="lg" />
                  <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
                </div>
              ) : (
                <MapContainer center={centerPosition} zoom={9} style={{ height: '100%', width: '100%' }} className="z-0">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapLocationButton language={language} />
                  <MarkerClusterGroup chunkedLoading iconCreateFunction={createActivityClusterIcon} maxClusterRadius={40}>
                    {activityMarkers.map((activity, idx) => (
                      <Marker key={idx} position={[activity.lat, activity.lon]} icon={orangeIcon}>
                        <Popup>
                          <div className="font-['Outfit'] min-w-[250px] max-w-[300px]">
                            <h3 className="font-bold text-[#252a34] mb-1 text-sm">{activity.nombre}</h3>
                            <Badge className="text-xs mb-2 bg-[#f97316]">{activity.tipo}</Badge>
                            {activity.descripcion && (
                              <p className="text-xs text-[#252a34]/70 mb-2 line-clamp-3">{activity.descripcion}</p>
                            )}
                            <div className="text-sm text-[#252a34]/70 space-y-1">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                <span className="text-xs">{activity.infraestructura}</span>
                              </div>
                              {activity.maximo_personas > 0 && (
                                <div className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  <span className="text-xs">{t.maxPeople}: {activity.maximo_personas}</span>
                                </div>
                              )}
                            </div>
                            <a
                              href={RESERVAS_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-3 flex items-center justify-center gap-1 w-full px-3 py-1.5 bg-[#f97316] text-white text-xs font-medium rounded hover:bg-orange-600 transition-colors"
                            >
                              <ExternalLink className="w-3 h-3" />
                              {t.moreInfo}
                            </a>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${activity.lat},${activity.lon}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 flex items-center justify-center gap-1 w-full px-3 py-1.5 bg-[#1a73e8] text-xs font-medium rounded hover:bg-[#1557b0] transition-colors"
                              style={{ color: 'white' }}
                            >
                              <Navigation className="w-3 h-3" style={{ color: 'white' }} />
                              {t.openInGoogleMaps}
                            </a>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MarkerClusterGroup>
                </MapContainer>
              )}
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentCategoryLoading ? (
              <div className="col-span-full flex flex-col items-center justify-center py-12">
                <Spinner size="lg" />
                <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
              </div>
            ) : (
              filteredActivities.map((activity, idx) => (
                <Card key={idx} className="card-hover border-slate-200">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 rounded-xl bg-[#f97316] flex items-center justify-center">
                        <Tent className="w-6 h-6 text-white" />
                      </div>
                      <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                        {activity.tipo}
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-[#252a34] mb-2 font-['Outfit'] line-clamp-2 text-sm">
                      {activity.nombre}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-[#252a34]/60 mb-2">
                      <MapPin className="w-4 h-4 text-[#f97316]" />
                      <span className="line-clamp-1 text-xs">{activity.infraestructura}</span>
                    </div>
                    {activity.maximo_personas > 0 && (
                      <div className="flex items-center gap-2 text-sm text-[#252a34]/60 mb-2">
                        <Users className="w-4 h-4 text-[#f97316]" />
                        <span className="text-xs">{t.maxPeople}: {activity.maximo_personas}</span>
                      </div>
                    )}
                    {activity.pernocta && (
                      <div className="flex items-center gap-2 text-sm text-[#252a34]/60 mb-2">
                        <Calendar className="w-4 h-4 text-[#f97316]" />
                        <span className="text-xs">{t.overnight}: {activity.pernocta}</span>
                      </div>
                    )}
                    <a
                      href={RESERVAS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 flex items-center justify-center gap-1 w-full px-3 py-2 bg-[#f97316]/10 text-[#f97316] text-xs font-medium rounded hover:bg-[#f97316]/20 transition-colors"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {t.moreInfo}
                    </a>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </>
      )}

      {/* RECREATIONAL AREAS SECTION */}
      {activeCategory === 'recreational' && (
        <>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
              <Input
                type="text"
                placeholder={t.searchRecreational}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 bg-white border-slate-200"
              />
            </div>
            <div className="text-sm text-[#252a34]/60 flex items-center gap-2">
              {t.showing} <strong className="text-[#8b5cf6]">{filteredRecreationalAreas.length}</strong> {t.of} {recreationalAreas.length} {t.recreationalResults}
            </div>
          </div>

          <Card className="overflow-hidden border-slate-200 mb-8">
            <div className="h-[350px] md:h-[400px]">
              {currentCategoryLoading ? (
                <div className="flex flex-col items-center justify-center h-full bg-slate-50">
                  <Spinner size="lg" />
                  <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
                </div>
              ) : (
                <MapContainer center={centerPosition} zoom={9} style={{ height: '100%', width: '100%' }} className="z-0">
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapLocationButton language={language} />
                  <MarkerClusterGroup chunkedLoading iconCreateFunction={createRecreationalClusterIcon} maxClusterRadius={40}>
                    {recreationalMarkers.map((area, idx) => (
                      <Marker key={area.id || idx} position={[area.lat, area.lon]} icon={violetIcon}>
                        <Popup>
                          <div className="font-['Outfit'] min-w-[250px] max-w-[300px]">
                            <h3 className="font-bold text-[#252a34] mb-1 text-sm">{area.nombre}</h3>
                            <Badge className="text-xs mb-2 bg-[#8b5cf6]">{area.tipo}</Badge>
                            <div className="text-sm text-[#252a34]/70 space-y-1">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                <span className="text-xs">{area.municipio}</span>
                              </div>
                              {area.espacio_natural && (
                                <div className="flex items-center gap-1">
                                  <Trees className="w-3 h-3 text-green-600" />
                                  <span className="text-xs text-green-700">{area.espacio_natural}</span>
                                </div>
                              )}
                              {area.puntos_interes && (
                                <p className="text-xs text-[#252a34]/60 mt-1 italic">{area.puntos_interes}</p>
                              )}
                            </div>
                            <a
                              href={`https://www.google.com/maps/search/?api=1&query=${area.lat},${area.lon}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-3 flex items-center justify-center gap-1 w-full px-3 py-1.5 bg-[#1a73e8] text-xs font-medium rounded hover:bg-[#1557b0] transition-colors"
                              style={{ color: 'white' }}
                            >
                              <Navigation className="w-3 h-3" style={{ color: 'white' }} />
                              {t.openInGoogleMaps}
                            </a>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MarkerClusterGroup>
                </MapContainer>
              )}
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {currentCategoryLoading ? (
              <div className="col-span-full flex flex-col items-center justify-center py-12">
                <Spinner size="lg" />
                <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
              </div>
            ) : (
              filteredRecreationalAreas.map((area, idx) => (
                <Card key={area.id || idx} className="card-hover border-slate-200">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 rounded-xl bg-[#8b5cf6] flex items-center justify-center">
                        <TreePine className="w-6 h-6 text-white" />
                      </div>
                      <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">
                        {area.tipo}
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-[#252a34] mb-2 font-['Outfit'] line-clamp-2 text-sm">
                      {area.nombre}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-[#252a34]/60 mb-2">
                      <MapPin className="w-4 h-4 text-[#8b5cf6]" />
                      <span className="line-clamp-1 text-xs">{area.municipio}</span>
                    </div>
                    {area.espacio_natural && (
                      <div className="flex items-center gap-2 text-sm mb-2">
                        <Trees className="w-4 h-4 text-green-600" />
                        <span className="line-clamp-1 text-xs text-green-700">{area.espacio_natural}</span>
                      </div>
                    )}
                    {area.puntos_interes && (
                      <p className="text-xs text-[#252a34]/60 mt-2 bg-slate-50 p-2 rounded line-clamp-2">
                        {area.puntos_interes}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
